import React from 'react';
import Barcode from './Barcode';
import ScaleWeighingSummary from './ScaleWeighingSummary';
import { FiInfo } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import { normalizeQuantity } from '../utils/quantity';
import { isScaleWeightMetricUnit } from '../utils/scalePresentation';

const truncateDescription = (value, maxLength = 80) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}...`;
};

const formatMoney = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;

const formatQuantity = (value) => {
  const quantity = normalizeQuantity(value);
  const hasDecimals = Math.abs(quantity - Math.round(quantity)) > 0.0001;
  return quantity.toLocaleString('de-DE', hasDecimals
    ? { minimumFractionDigits: 0, maximumFractionDigits: 3 }
    : undefined);
};

const resolveVarietyDescription = (variety) =>
  String(variety?.description || '').trim();

const isSameScaleSelection = (selection, varietyId, priceIndex, price) => {
  if (!selection) return false;
  if (Number(selection.varietyId) !== Number(varietyId)) return false;
  if (Number.isInteger(selection.priceIndex) && Number(selection.priceIndex) === Number(priceIndex)) {
    return true;
  }
  return selection.varietyPriceId != null && Number(selection.varietyPriceId) === Number(price?.id);
};

const VarietyDetailModal = ({
  variety,
  onClose,
  handleClick,
  allowSoldOutSelection = false,
  priceFilter,
  resolveDisplayPrice,
  quantities = {},
  discounts = {},
  activeScaleSelection = null,
  scaleWeightKg = null,
  onPriceIncrement,
  onPriceDecrement,
  onPriceKeypad,
  onConfirmClose,
}) => {
  const [selectedPriceKey, setSelectedPriceKey] = React.useState(null);
  React.useEffect(() => {
    setSelectedPriceKey(null);
  }, [variety?.id]);

  const getPriceSelectionKey = (price, priceIndex) => (
    `${variety?.id ?? 'variety'}:${price?.id ?? priceIndex ?? 'price'}:${priceIndex ?? ''}`
  );
  const hasCardActions = (
    typeof onPriceIncrement === 'function'
    || typeof onPriceDecrement === 'function'
    || typeof onPriceKeypad === 'function'
  );
  const actClick = (price, priceIndex, isDisabled) => {
    if (isDisabled) return;
    setSelectedPriceKey(getPriceSelectionKey(price, priceIndex));
    if (handleClick) {
      handleClick(price);
    }
  };
  const sellablePrices = (variety.VarietyPrices || [])
    .map((price, priceIndex) => ({ price, priceIndex }))
    .filter(({ price }) => price.price !== null && price.price !== undefined);
  const filteredPrices = sellablePrices.filter(({ price }) =>
    priceFilter ? priceFilter(price) : true
  );
  const pricedEntries = filteredPrices.map(({ price, priceIndex }) => {
    const unitDiscount = Number(discounts?.[variety?.id]?.[priceIndex] || 0);
    const display = typeof resolveDisplayPrice === 'function'
      ? resolveDisplayPrice(variety, price, unitDiscount)
      : null;
    const shownPrice = Number.isFinite(Number(display?.price))
      ? Number(display.price)
      : Number(price.price || 0) - unitDiscount;
    return {
      price,
      priceIndex,
      shownPrice,
      taxFreeApplied: Boolean(display?.taxFreeApplied),
      quantity: normalizeQuantity(quantities?.[variety?.id]?.[priceIndex] || 0),
    };
  });
  const sortedPricedEntries = pricedEntries
    .slice()
    .sort((entryA, entryB) => entryB.shownPrice - entryA.shownPrice);
  const varietyDescription = resolveVarietyDescription(variety);
  const isVarietySoldOut = sellablePrices.length > 0 && sellablePrices.every(({ price }) => price.isSoldOut);
  const hasScalePrices = pricedEntries.some(({ price }) => (
    price?.useScaleWeight && isScaleWeightMetricUnit(price?.metricUnit)
  ));
  const activeScaleEntry = pricedEntries.find(({ price, priceIndex }) => (
    price?.useScaleWeight
    && isScaleWeightMetricUnit(price?.metricUnit)
    && isSameScaleSelection(activeScaleSelection, variety?.id, priceIndex, price)
  ));
  const scaleSummaryWeightKg = Number(scaleWeightKg);
  const scaleSummaryUnitPrice = activeScaleEntry ? activeScaleEntry.shownPrice : null;
  const selectedPriceEntry = pricedEntries.find(({ price, priceIndex }) => (
    selectedPriceKey === getPriceSelectionKey(price, priceIndex)
  ));
  const selectedPriceIsScale = Boolean(
    selectedPriceEntry?.price?.useScaleWeight
    && isScaleWeightMetricUnit(selectedPriceEntry?.price?.metricUnit)
  );
  const summaryMode = selectedPriceEntry && !selectedPriceIsScale
    ? 'quantity'
    : hasScalePrices
      ? 'scale'
      : null;
  const summaryThemeClass = summaryMode === 'quantity'
    ? 'border-blue-100 bg-blue-50/80 text-blue-950'
    : 'border-emerald-100 bg-emerald-50/80 text-emerald-950';
  const summaryTitleClass = summaryMode === 'quantity'
    ? 'text-blue-700'
    : 'text-emerald-700';

  const renderPriceCard = (entry) => {
    const {
      price,
      priceIndex,
      shownPrice,
      taxFreeApplied,
      quantity,
    } = entry;
    const priceKey = getPriceSelectionKey(price, priceIndex);
    const isDisabled = price.isSoldOut && !allowSoldOutSelection;
    const isScalePrice = Boolean(price?.useScaleWeight && isScaleWeightMetricUnit(price?.metricUnit));
    const isSelectedScalePrice = isScalePrice && isSameScaleSelection(activeScaleSelection, variety?.id, priceIndex, price);
    const isSelectedPrice = selectedPriceKey === priceKey;
    const hasCartQuantity = normalizeQuantity(quantity) > 0;
    const quantityLabel = formatQuantity(quantity);
    const subtotal = Math.floor(shownPrice * quantity);
    const description = truncateDescription(price.description || varietyDescription) || '';
    const handleCardAction = (callback) => {
      if (isDisabled) return;
      setSelectedPriceKey(priceKey);
      callback?.(price, priceIndex);
    };

    return (
      <article
        key={price?.id || priceIndex}
        aria-disabled={isDisabled}
        className={`relative overflow-hidden rounded-xl border bg-white shadow-sm transition ${
          isDisabled
            ? 'cursor-not-allowed border-gray-200 opacity-70'
            : isSelectedScalePrice
              ? 'border-emerald-300 shadow-md ring-2 ring-emerald-100'
              : isSelectedPrice
                ? 'border-blue-300 shadow-md ring-2 ring-blue-100'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
        }`}
      >
        {hasCardActions ? (
          <div className="absolute inset-0 grid grid-cols-2">
            <button
              type="button"
              onClick={() => handleCardAction(onPriceDecrement)}
              disabled={isDisabled}
              className={`min-h-full border-r border-transparent transition ${
                isDisabled ? 'cursor-not-allowed bg-gray-50/40' : 'bg-red-50/0 hover:bg-red-50/70'
              }`}
              title="Click a la izquierda para restar"
              aria-label={`Restar ${price.metricUnit || 'precio'}`}
            />
            <button
              type="button"
              onClick={() => handleCardAction(onPriceIncrement)}
              disabled={isDisabled}
              className={`min-h-full transition ${
                isDisabled ? 'cursor-not-allowed bg-gray-50/40' : 'bg-emerald-50/0 hover:bg-emerald-50/70'
              }`}
              title="Click a la derecha para sumar"
              aria-label={`Sumar ${price.metricUnit || 'precio'}`}
            />
          </div>
        ) : (
          <button
            type="button"
            aria-label={`Seleccionar ${price.metricUnit || 'precio'}`}
            onClick={() => actClick(price, priceIndex, isDisabled)}
            disabled={isDisabled}
            className="absolute inset-0"
          />
        )}

        {price.isSoldOut && (
          <span className="absolute right-2 top-2 z-20 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
            Agotado
          </span>
        )}
        {isSelectedScalePrice && (
          <span className="absolute left-1/2 top-2 z-20 -translate-x-1/2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
            Seleccionado
          </span>
        )}

        <div className="relative z-10 pointer-events-none p-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="relative shrink-0">
              <img
                src={price.priceImage || variety.varietyImage || 'https://placehold.co/100'}
                alt={variety.varietyName}
                className="h-14 w-14 rounded-lg object-cover shadow"
              />
              {hasCartQuantity && (
                <span
                  className="absolute -right-2 -top-2 flex min-h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1.5 text-xs font-black leading-none text-white shadow"
                  title={`Cantidad: ${quantityLabel}`}
                  aria-label={`Cantidad: ${quantityLabel}`}
                >
                  {quantityLabel}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-700">{price.metricUnit}</p>
              <span className="inline-flex items-center text-lg font-bold text-gray-900">
                {formatMoney(shownPrice)}
                {taxFreeApplied && (
                  <span
                    className="ml-1 inline-flex text-blue-700"
                    title="Precio ajustado sin impuestos excluidos."
                    aria-label="Precio ajustado sin impuestos excluidos."
                  >
                    <FiInfo size={13} />
                  </span>
                )}
              </span>
            </div>
            {hasCardActions && (
              <button
                type="button"
                onClick={() => handleCardAction(onPriceKeypad)}
                disabled={isDisabled}
                className={`pointer-events-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${
                  isDisabled
                    ? 'border-gray-200 bg-gray-100 text-gray-400'
                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                title="Cantidad exacta"
                aria-label="Cantidad exacta"
              >
                <FaCalculator size={16} />
              </button>
            )}
          </div>

          <p className="mt-3 min-h-[2.5rem] break-all text-sm leading-snug text-gray-600">
            {description || '\u200e'}
          </p>

          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <div className="text-sm font-bold text-gray-900">
              Subtotal: {formatMoney(subtotal)}
            </div>
          </div>

          {price.barcode ? (
            <Barcode value={price.barcode} className="mt-3" />
          ) : (
            <p className="mt-3 text-center text-xs text-gray-500">
              Sin codigo de barras
            </p>
          )}

        </div>
      </article>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden border border-gray-100 max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Variedad</p>
            <h2 className="text-lg font-semibold text-gray-900">{variety.varietyName}</h2>
            {isVarietySoldOut && (
              <span className="mt-1 inline-flex items-center rounded-full bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5">
                AGOTADO
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
          >
            X
          </button>
        </div>

        <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 sm:gap-6 max-h-[78vh] overflow-y-auto">
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 shadow-sm">
              <div className="text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Imagen</div>
              <div className="relative mx-auto w-20 h-20 sm:w-28 sm:h-28 lg:w-full lg:aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                <img
                  src={variety.varietyImage || 'https://placehold.co/400'}
                  alt={variety.varietyName}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
            {variety.varietyName && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="text-xs font-semibold text-gray-600 uppercase">Nombre</div>
                <p className="mt-2 text-sm text-gray-700">{variety.varietyName}</p>
              </div>
            )}
            {varietyDescription && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="text-xs font-semibold text-gray-600 uppercase">Descripcion</div>
                <p
                  className="mt-2 text-sm text-gray-700 break-words"
                  title={varietyDescription}
                >
                  {truncateDescription(varietyDescription, 140)}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Precios disponibles</h3>
            </div>
            {summaryMode && (
              <div className={`rounded-2xl border p-3 shadow-sm ${summaryThemeClass}`}>
                <div className={`text-xs font-semibold uppercase tracking-wide ${summaryTitleClass}`}>
                  {summaryMode === 'quantity' ? 'Precio seleccionado' : 'Lectura de balanza'}
                </div>
                <ScaleWeighingSummary
                  weightKg={summaryMode === 'quantity'
                    ? selectedPriceEntry?.quantity ?? 0
                    : Number.isFinite(scaleSummaryWeightKg) && scaleSummaryWeightKg >= 0 ? scaleSummaryWeightKg : null}
                  unitPrice={summaryMode === 'quantity' ? selectedPriceEntry?.shownPrice : scaleSummaryUnitPrice}
                  amountTitle={summaryMode === 'quantity' ? 'Cantidad' : 'Peso'}
                  amountLabel={summaryMode === 'quantity'
                    ? formatQuantity(selectedPriceEntry?.quantity ?? 0)
                    : undefined}
                  allowZeroAmount
                  className="mt-3 gap-2"
                  cardClassName="rounded-xl bg-white/75 px-3 py-2"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedPricedEntries.length > 0 ? (
                sortedPricedEntries.map(renderPriceCard)
              ) : (
                <p className="col-span-full text-sm text-gray-500 text-center">
                  Sin precio disponible
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end border-t border-gray-100 bg-white px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onConfirmClose || onClose}
            className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default VarietyDetailModal;
